import type { ProductFormulation } from '@/constants/products';
import { roundDispenseLimit } from '@/utils/dosageLimit/dispenseLimit';
import type { StateCreator } from 'zustand';
import type { PrescriptionAssistantSliceType } from '../types';
import { setRevalidatedPad } from '../utils/setRevalidatedPad';

/**
 * Stores the typed limit at the pad's precision; a changed limit is unconfirmed, so it asks for acknowledgement again,
 * and one on a formulation without a row drops at once. Every item is re-validated: its dosage text carries the limit.
 */
export const setFormulationDispenseLimit: StateCreator<
  PrescriptionAssistantSliceType,
  [['zustand/immer', never], ['zustand/devtools', never]],
  [],
  (formulation: ProductFormulation, limit: number) => void
> = (set, get) => (formulation, limit) => {
  const { prescriptionPad, prescriptionPadRules } = get().prescriptionAssistant;
  const roundedLimit = roundDispenseLimit(limit);
  const setLimit = prescriptionPad.dispenseLimits[formulation];
  // The input writes on every keystroke, so retyping the confirmed value must not ask again
  const isConfirmed = setLimit?.limit === roundedLimit && setLimit.isConfirmed;
  setRevalidatedPad(
    set,
    {
      ...prescriptionPad,
      dispenseLimits: {
        ...prescriptionPad.dispenseLimits,
        [formulation]: { limit: roundedLimit, isConfirmed },
      },
    },
    prescriptionPadRules,
    'prescriptionPad/setFormulationDispenseLimit',
  );
};
